import { type FetchPoolsParams, pumpPoolModel } from '@models/pump-pool.model';
import { useModel } from 'foca';
import type { OpenapiClientPump } from 'src/openapi/pump';
import { throttle } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useRefreshFlag } from './use-refresh';

const fetchPools = throttle(pumpPoolModel.fetchPools, 10, { leading: true, trailing: false });

export const usePumpPools = (options: FetchPoolsParams & { reload: number; loopTime?: number }) => {
  const { reload, loopTime = 30000, ...params } = options;
  const [pools, setPools] = useState<OpenapiClientPump.GetPoolsResponse>({
    page: 1,
    pageSize: 10,
    total: 0,
    king: null,
    featured: null,
    data: [],
  });
  const [loading, setLoading] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    isUnmountedRef.current = false;
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    if (!loopTime) {
      return;
    }

    const loop = async (isInitial: boolean) => {
      if (isInitial) {
        setLoading(true);
      }
      try {
        const result = await fetchPools(params);

        if (!isUnmountedRef.current && requestIdRef.current === currentRequestId) {
          setPools(result);
          setLoading(false);

          timerRef.current = setTimeout(() => {
            loop(false);
          }, loopTime);
        }
      } catch (error) {
        if (requestIdRef.current === currentRequestId) {
          setLoading(false);
        }
      }
    };
    loop(true);
    return () => {
      isUnmountedRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [JSON.stringify(options), reload, loopTime]);

  return { loading, pools };
};

const fetchPool = throttle(pumpPoolModel.fetchPoolByTokenAddress, 10, {
  leading: true,
  trailing: false,
});

export const usePumpPool = (tokenAddress: string) => {
  const { refresh } = useRefreshFlag();
  const pool = useModel(pumpPoolModel, (state) => state.poolMap[tokenAddress]);
  useEffect(() => {
    tokenAddress && fetchPool(tokenAddress);
  }, [tokenAddress, refresh]);
  return pool;
};
